import 'custom-event-polyfill'
import { Subject, fromEvent, merge } from 'rxjs'
import { map, tap, startWith, filter } from 'rxjs/operators'
import { getBaseURI, getLink } from '../helpers/document'
import { result, replace, startsWith, includes } from '../libraries/lodash'
import debug from './debug'
import { URL_LOGOUT } from 'constants/urls'

export const ROOT_URL = '/'

const createSyntheticEvent = eventName => {
  const event = new CustomEvent(eventName, { bubbles: true })
  event.state = window.history.state
  return event
}

export const history = (window => {
  const { history, dispatchEvent } = window
  return {
    push: url => history.pushState({}, '', url),
    replace: url => {
      history.replaceState({}, '', url)
      dispatchEvent(createSyntheticEvent('replacestate'))
    }
  }
})(window)

const trim = href => replace(href, getBaseURI(), '') || ''
export const getPageUrl = href => trim(href).split('?')[0] // TODO: maybe not best place for this?

const HistoryStore = {}

export const getHistoryState = function () {
  try {
    const stateId = result(window, 'history.state.stateId', null)
    const state =
      (stateId &&
        ((Object.hasOwn(HistoryStore, stateId) && HistoryStore[stateId]) ||
          (Object.hasOwn(sessionStorage, stateId) &&
            (HistoryStore[stateId] = JSON.parse(
              sessionStorage.getItem(stateId)
            ))))) ||
      null
    return state || {}
  } catch (e) {
    return {}
  }
}

export const updateHistoryState = function (stateUpdate) {
  const href = location.href
  const newState = Object.assign(getHistoryState(), stateUpdate, { href })

  try {
    replaceState(newState)
  } catch (e) {
    debug.error(e)
  }
}

const generateStateId = function () {
  const s = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)
  return s() + s() + '-' + s() + s() + s()
}

const replaceState = function (state, forceNew = false) {
  let stateId = result(window, 'history.state.stateId', '')

  if (forceNew || !stateId) {
    stateId = generateStateId()
    window.history.replaceState(
      { stateId },
      window.location.href,
      window.location.href
    )
  }
  try {
    window.sessionStorage.setItem(stateId, JSON.stringify(state))
  } catch (e) {}
  HistoryStore[stateId] = state
  return true
}

const historyEvent = function () {
  const state = getHistoryState()
  const href = result(state, 'href', null)
  const {
    scrollMain = null,
    scrollLeft = null,
    scrollRight = null,
    scrollHeight = 0,
    pageView = 'main',
    pageData = {},
    pageState = {}
  } =
    (href === location.href && state) || {}

  const [url, queryString = {}] = trim(location.href).split('?')

  return {
    scrollLeft,
    scrollMain,
    scrollRight,
    scrollHeight,
    pageView,
    url,
    queryString,
    pageData,
    pageState,
    href
  }
}

export const pushState = new Subject()

export const getBaseUrl = function (prefixed = true) {
  const baseEl = document.querySelector('base')
  const href = baseEl ? baseEl.getAttribute('href') : '/'
  return prefixed
    ? location.protocol + '//' + location.host + href
    : href
}

const navigationChange = fromEvent(window, 'popstate')
  .pipe(
    startWith(true),
    map(historyEvent),
    tap(updateHistoryState)
  )

const replaceRoute = fromEvent(window, 'replacestate')
  .pipe(
    startWith(true),
    map(historyEvent),
    tap(updateHistoryState)
  )

const internalLinkRules = ({ event, linkElement }) => {
  if (event.which > 1 || event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return false
  if (!linkElement) return false
  const href = linkElement.getAttribute('href')
  const target = linkElement.getAttribute('target')
  return (
    startsWith(linkElement.href, getBaseUrl()) &&
    href !== URL_LOGOUT &&
    !startsWith(href, 'javascript:') &&
    !startsWith(href, '/') &&
    (!startsWith(href, 'http') || startsWith(href, getBaseUrl(true))) &&
    !startsWith(href, 'api/') &&
    !includes(href, '/api/') &&
    !(/\.[a-z]+$/).test(href) &&
    href !== URL_LOGOUT &&
    target !== '_blank'
  )
}

const $linkClicks = merge(
  fromEvent(window, 'click').pipe(
    map(event => ({ linkElement: getLink(event.target), event })),
    filter(internalLinkRules),
    tap(({ event }) => event.preventDefault()),
    map(({ linkElement }) => linkElement.getAttribute('href'))
  ),
  pushState)
  .pipe(tap(url => history.push(url)))

$linkClicks
  .subscribe(() => window.dispatchEvent(createSyntheticEvent('popstate')))

export const urlChange = merge(replaceRoute, navigationChange)

export const observe = function () {
  return navigationChange
}

export const pushUrl = function (url) {
  try {
    pushState.next(url)
    const href = location.href
    replaceState({ href })
    window.dispatchEvent(createSyntheticEvent('popstate'))
  } catch (e) {
    debug.error(e)
    window.location.reload()
  }
}

export const replaceUrl = function (url) {
  try {
    history.replace(url)
    const href = location.href
    replaceState({ href })
    window.dispatchEvent(createSyntheticEvent('popstate'))
  } catch (e) {
    debug.error(e)
    window.location.reload()
  }
}

// navigationChange.subscribe(debug.log);
